/**
 * UpdateDetails
 *
 * Main profile update page component.
 *
 * Responsibilities:
 * - Renders sidebar navigation for profile sections (Details, Social, Interests, Password).
 * - Displays the appropriate form based on the selected section.
 * - Uses ProfileUpdateContext to access user data, loading/error status, and dropdown options.
 * - Handles loading, error, and empty user data states.
 * - Provides a responsive layout for both mobile and desktop.
 */

import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import UpdateDetailsForm from "./UpdateDetailsForm";
import UpdateSocialForm from "./UpdateSocialForm";
import UpdateInterestsForm from "./UpdateInterestsForm";
import UpdatePasswordForm from "./UpdatePasswordForm";
import { useProfileUpdateContext } from "../../contexts/ProfileUpdateContext";
import {
  pageContainer,
  bodyLayout,
  sidebarContainer,
  sidebarInner,
  sidebarButton,
  contentContainer,
  formContainer,
  sectionTitle,
  formInner,
} from "./styles";

// Sidebar navigation items for profile sections
const NAV = [
  { id: "details", label: "Details" },
  { id: "social", label: "Social" },
  { id: "interests", label: "Interests" },
  { id: "password", label: "Password" },
];

/**
 * UpdateDetails React component.
 *
 * Renders the main profile update interface, including navigation and forms.
 * Handles loading, error, and empty states using context.
 *
 * @returns {JSX.Element} The rendered component.
 */
export default function UpdateDetails() {
  // State: Tracks which profile section is currently active
  const [activeSection, setActiveSection] = useState("details");

  // Context: Provides user profile data, loading/error status, and dropdown options
  const { profileData: userData, status } = useProfileUpdateContext();

  // Show loading spinner if data is loading and no user data is present
  if (status.loading && !userData)
    return (
      <Box sx={{ p: 4 }}>
        <CircularProgress />
      </Box>
    );

  // Show error alert if there is an error in fetching data
  if (status.error)
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{status.error}</Alert>
      </Box>
    );

  // Show warning if no user data is available
  if (!userData)
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="warning">
          No user data found. Please try logging in again.
        </Alert>
      </Box>
    );

  // Main layout rendering
  return (
    <Box sx={pageContainer}>
      {/* Body: Contains sidebar and content */}
      <Box sx={bodyLayout}>
        {/* Sidebar: Navigation for profile sections */}
        <Box sx={sidebarContainer}>
          <Box sx={sidebarInner}>
            {/* Navigation buttons for each section */}
            {NAV.map((item) => (
              <Button
                key={item.id}
                variant={activeSection === item.id ? "contained" : "outlined"}
                color="primary"
                onClick={() => setActiveSection(item.id)}
                sx={sidebarButton(activeSection === item.id)}
                fullWidth
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Content: Displays the selected form */}
        <Box sx={contentContainer}>
          <Box sx={formContainer}>
            {/* Section title */}
            <Typography
              variant="h6"
              color="primary"
              fontWeight={700}
              sx={sectionTitle}
            >
              {activeSection === "details" && "Account Settings"}
              {activeSection === "social" && "Update Social"}
              {activeSection === "interests" && "Update Interests"}
              {activeSection === "password" && "Update Password"}
            </Typography>
            <Box sx={formInner}>
              {/* Render the correct form based on active section */}
              {activeSection === "details" && <UpdateDetailsForm />}
              {activeSection === "social" && <UpdateSocialForm {...userData} />}
              {activeSection === "interests" && (
                <UpdateInterestsForm
                  {...userData}
                  // Ensure keywords are passed as a comma-separated string
                  keywords={
                    typeof userData.keywords === "string"
                      ? userData.keywords
                      : Array.isArray(userData.keywords)
                      ? userData.keywords
                          .map((k: any) => k.value || k)
                          .join(", ")
                      : ""
                  }
                />
              )}
              {activeSection === "password" && <UpdatePasswordForm />}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
